import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Wrench,
  ShoppingBag,
  FileText,
  ShieldCheck,
  ChevronRight,
  ChevronLeft,
  SlidersHorizontal,
  Keyboard,
} from "lucide-react";

// Modals
import PosRepairsModal from "./PosRepairsModal";
import PosSalesModal from "./PosSalesModal";
import PosCreateQuotationModal from "./PosCreateQuotationModal";
import PosWarrantyModal from "./PosWarrantyModal";

const PosActionsSidebar = ({
  mode = "pos", // "pos" | "repair"
  currentProducts = [],
  customer = null,
  currencySymbol = "$",
  onLoadSaleToCart,
  onSelectRepair,
  onClearCart,
  currentSaleData = null,
  onSelectSaleItemForWarranty,
}) => {
  // Sidebar collapsed state (desktop), remembered in localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem("pos_sidebar_collapsed");
      return saved !== null ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });

  const toggleCollapsed = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("pos_sidebar_collapsed", JSON.stringify(next));
      } catch {
        // ignore
      }
      return next;
    });
  };

  // Mobile sheet state (< lg screens)
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);

  // Sub-modals state
  const [repairsModalOpen, setRepairsModalOpen] = useState(false);
  const [salesModalOpen, setSalesModalOpen] = useState(false);
  const [quotationModalOpen, setQuotationModalOpen] = useState(false);
  const [warrantyModalOpen, setWarrantyModalOpen] = useState(false);

  // Keyboard shortcuts: Alt+R, Alt+S, Alt+Q, Alt+W
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.altKey) {
        if (e.key === "r" || e.key === "R") {
          e.preventDefault();
          setRepairsModalOpen(true);
        } else if (e.key === "s" || e.key === "S") {
          e.preventDefault();
          setSalesModalOpen(true);
        } else if ((e.key === "q" || e.key === "Q") && mode === "pos") {
          e.preventDefault();
          setQuotationModalOpen(true);
        } else if (e.key === "w" || e.key === "W") {
          e.preventDefault();
          setWarrantyModalOpen(true);
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [mode]);

  const handleOpenWarrantyFromSale = async (sale) => {
    if (onSelectSaleItemForWarranty) {
      await onSelectSaleItemForWarranty(sale, null);
    } else if (onLoadSaleToCart) {
      await onLoadSaleToCart(sale);
    }
  };

  const isRetailPos = mode === "pos";
  const cartItemCount = currentProducts?.length || 0;

  // Actions list configuration
  const actionItems = [
    {
      id: "repairs",
      title: "View Repairs",
      subtitle: "Jobs, labels & notes",
      icon: Wrench,
      shortcut: "Alt+R",
      onClick: () => {
        setMobileSheetOpen(false);
        setRepairsModalOpen(true);
      },
      show: true,
    },
    {
      id: "sales",
      title: "View Sales",
      subtitle: "Invoices & balance payments",
      icon: ShoppingBag,
      shortcut: "Alt+S",
      onClick: () => {
        setMobileSheetOpen(false);
        setSalesModalOpen(true);
      },
      show: true,
    },
    {
      id: "quotation",
      title: "Create Quotation",
      subtitle: "From current cart",
      icon: FileText,
      shortcut: "Alt+Q",
      badge: cartItemCount > 0 ? cartItemCount : null,
      onClick: () => {
        setMobileSheetOpen(false);
        setQuotationModalOpen(true);
      },
      show: isRetailPos,
    },
    {
      id: "warranty",
      title: "Warranty Claims",
      subtitle: "Sales warranty workflow",
      icon: ShieldCheck,
      shortcut: "Alt+W",
      onClick: () => {
        setMobileSheetOpen(false);
        setWarrantyModalOpen(true);
      },
      show: true,
    },
  ].filter((item) => item.show);

  return (
    <>
      {/* ─────────────────────────────────────────────────────────────────────────
          Desktop Proper Sidebar: Integrated into POS flex layout row
         ───────────────────────────────────────────────────────────────────────── */}
      <aside
        className={`hidden lg:flex flex-col h-full border-l border-border bg-card shrink-0 transition-[width] duration-200 select-none ${isCollapsed ? "w-14" : "w-56"
          }`}
      >
        {/* Header */}
        <div className="h-12 border-b border-border flex items-center justify-between shrink-0 bg-card px-2">
          {!isCollapsed ? (
            <div className="flex items-center justify-between w-full pl-1">
              <div className="flex items-center gap-2 min-w-0">
                <SlidersHorizontal className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground truncate">
                  Actions
                </span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                onClick={toggleCollapsed}
                title="Collapse sidebar"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 mx-auto text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg cursor-pointer"
                    onClick={toggleCollapsed}
                    title="Expand sidebar"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left">
                  <span>Expand Actions</span>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        {/* Action Items List */}
        {!isCollapsed ? (
          /* Expanded state: Full buttons matching system sidebar */
          <div className="p-2 flex-1 overflow-y-auto">
            <div className="px-2 pt-1 pb-2">
              <h3 className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70">
                Quick Actions
              </h3>
            </div>
            <div className="space-y-0.5">
              {actionItems.map((item, idx) => (
                <React.Fragment key={item.id}>
                  {idx > 0 && <Separator className="my-1.5 bg-border/50" />}
                  <Button
                    variant="ghost"
                    onClick={item.onClick}
                    className="w-full justify-start h-11 px-3 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors group cursor-pointer"
                    title={`${item.title} (${item.shortcut})`}
                  >
                    <item.icon className="h-5 w-5 mr-3 text-muted-foreground group-hover:text-foreground shrink-0 transition-colors" />
                    <span className="truncate flex-1 text-left font-medium">{item.title}</span>
                    {item.badge != null ? (
                      <Badge
                        variant="secondary"
                        className="text-[10px] px-1.5 py-0 h-4 bg-primary/10 text-primary font-semibold border-0 ml-1.5"
                      >
                        {item.badge}
                      </Badge>
                    ) : (
                      <kbd className="ml-1.5 pointer-events-none inline-flex h-4 items-center rounded border border-border/60 bg-muted/60 px-1 font-mono text-[9px] font-medium text-muted-foreground/80">
                        {item.shortcut}
                      </kbd>
                    )}
                  </Button>
                </React.Fragment>
              ))}
            </div>
          </div>
        ) : (
          /* Collapsed state: Icon rail with Tooltips & Dividers */
          <TooltipProvider delayDuration={100}>
            <div className="py-1 px-1.5 flex-1 overflow-y-auto flex flex-col items-center">
              {actionItems.map((item, idx) => (
                <React.Fragment key={item.id}>
                  {idx > 0 && <Separator className="w-7 my-2 bg-border/70" />}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={item.onClick}
                        className="h-10 w-10 text-muted-foreground hover:text-foreground hover:bg-accent relative rounded-lg cursor-pointer"
                      >
                        <item.icon className="h-5 w-5" />
                        {item.badge != null && (
                          <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-primary" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="flex items-center gap-2">
                      <span className="font-medium">{item.title}</span>
                      <kbd className="text-[10px] opacity-75 font-mono bg-black/20 dark:bg-white/20 px-1 py-0.5 rounded">
                        {item.shortcut}
                      </kbd>
                    </TooltipContent>
                  </Tooltip>
                </React.Fragment>
              ))}
            </div>
          </TooltipProvider>
        )}

        {/* Footer shortcuts hint (expanded only) */}
        {!isCollapsed && (
          <div className="p-2.5 border-t border-border bg-muted/20 text-[10px] text-muted-foreground flex items-center justify-center gap-1.5 shrink-0">
            <Keyboard className="h-3.5 w-3.5 shrink-0" />
            <span>Alt + R / S / Q / W</span>
          </div>
        )}
      </aside>

      {/* ─────────────────────────────────────────────────────────────────────────
          Mobile Floating Drawer Trigger (< lg screens)
         ───────────────────────────────────────────────────────────────────────── */}
      <button
        type="button"
        className="lg:hidden fixed right-3 bottom-20 z-40 h-10 w-10 rounded-full bg-card border border-border shadow-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent cursor-pointer transition-colors"
        onClick={() => setMobileSheetOpen(true)}
        title="Open Actions"
      >
        <SlidersHorizontal className="h-4 w-4" />
      </button>

      <Sheet open={mobileSheetOpen} onOpenChange={setMobileSheetOpen}>
        <SheetContent side="right" className="w-64 p-0 bg-card border-l border-border flex flex-col">
          <SheetHeader className="h-12 px-4 border-b border-border flex flex-row items-center justify-between space-y-0">
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
              <SheetTitle className="font-semibold text-xs uppercase tracking-wider text-muted-foreground">
                Actions
              </SheetTitle>
            </div>
          </SheetHeader>
          <div className="p-3 flex-1 overflow-y-auto">
            {actionItems.map((item, idx) => (
              <React.Fragment key={item.id}>
                {idx > 0 && <Separator className="my-1.5 bg-border/50" />}
                <Button
                  variant="ghost"
                  onClick={item.onClick}
                  className="w-full justify-start h-11 px-3 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors group cursor-pointer"
                >
                  <item.icon className="h-5 w-5 mr-3 text-muted-foreground group-hover:text-foreground shrink-0" />
                  <span className="truncate flex-1 text-left font-medium">{item.title}</span>
                  {item.badge != null && (
                    <Badge
                      variant="secondary"
                      className="text-[10px] px-1.5 py-0 h-4 bg-primary/10 text-primary font-semibold border-0"
                    >
                      {item.badge}
                    </Badge>
                  )}
                </Button>
              </React.Fragment>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      {/* ─────────────────────────────────────────────────────────────────────────
          Modals
         ───────────────────────────────────────────────────────────────────────── */}
      {repairsModalOpen && (
        <PosRepairsModal
          show={repairsModalOpen}
          onHide={() => setRepairsModalOpen(false)}
          currencySymbol={currencySymbol}
          onSelectRepair={onSelectRepair}
          mode={mode}
        />
      )}

      {salesModalOpen && (
        <PosSalesModal
          show={salesModalOpen}
          onHide={() => setSalesModalOpen(false)}
          currencySymbol={currencySymbol}
          onLoadSaleToCart={onLoadSaleToCart}
          onOpenWarrantyClaim={handleOpenWarrantyFromSale}
          mode={mode}
        />
      )}

      {quotationModalOpen && isRetailPos && (
        <PosCreateQuotationModal
          show={quotationModalOpen}
          onHide={() => setQuotationModalOpen(false)}
          currentProducts={currentProducts}
          customer={customer}
          currencySymbol={currencySymbol}
          onClearCart={onClearCart}
        />
      )}

      {warrantyModalOpen && (
        <PosWarrantyModal
          show={warrantyModalOpen}
          onHide={() => setWarrantyModalOpen(false)}
          currencySymbol={currencySymbol}
          onLoadSaleToCart={onLoadSaleToCart}
          onSelectSaleItemForWarranty={onSelectSaleItemForWarranty}
          currentSaleData={currentSaleData}
          mode={mode}
        />
      )}
    </>
  );
};

PosActionsSidebar.propTypes = {
  mode: PropTypes.oneOf(["pos", "repair"]),
  currentProducts: PropTypes.array,
  customer: PropTypes.object,
  currencySymbol: PropTypes.string,
  onLoadSaleToCart: PropTypes.func,
  onSelectRepair: PropTypes.func,
  onClearCart: PropTypes.func,
  currentSaleData: PropTypes.object,
  onSelectSaleItemForWarranty: PropTypes.func,
};

export default PosActionsSidebar;
